import { useState } from 'react'
import { useFormik } from 'formik'
import * as yup from 'yup'
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Modal,
  TextField,
  Typography,
} from '@mui/material'
import { FaEye, FaEyeSlash, FaHourglassHalf } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import { useToast } from 'context/ToastContext'
import { useAuth } from 'context/AuthContext'
import { restoreAccount as restoreAccountApi } from 'services/authService'

interface Props {
  open: boolean
  onClose: () => void
}

const RestoreAccountModal = ({ open, onClose }: Props) => {
  const { t } = useTranslation()
  const { showToast } = useToast()
  const { restoreAccount } = useAuth()
  const [showPassword, setShowPassword] = useState(false)

  const schema = yup.object({
    password: yup.string().required(t('auth.password-required')),
  })

  const formik = useFormik({
    initialValues: { password: '' },
    validationSchema: schema,
    onSubmit: async (values, { setFieldError, setSubmitting }) => {
      try {
        const res = await restoreAccountApi(values.password)
        restoreAccount(res.data.user, res.data.accessToken)
        showToast('success', t('settings.restore-account-modal.success'))
        onClose()
      } catch (err: any) {
        const status = err?.response?.data?.status
        if (status === -4) {
          setFieldError('password', t('settings.restore-account-modal.error.wrong-password'))
        } else {
          showToast('error', t('settings.restore-account-modal.error.bad-request'))
        }
      } finally {
        setSubmitting(false)
      }
    },
  })

  const handleClose = () => {
    if (formik.isSubmitting) return
    formik.resetForm()
    setShowPassword(false)
    onClose()
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        className="delete-account-modal-box"
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 24,
        }}
      >
        <Typography variant="h6" color="primary">
          {t('settings.restore-account-modal.title')}
        </Typography>

        <Box
          className="delete-account-warning-block"
          sx={{ bgcolor: 'rgba(255,255,255,0.04)', opacity: 1 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FaHourglassHalf size={14} color='#fff' />
            <Typography variant="body2" fontWeight={600} sx={{ color: 'text.primary' }}>
              {t('settings.restore-account-modal.warning-title')}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('settings.restore-account-modal.warning-body')}
          </Typography>
        </Box>

        <form onSubmit={formik.handleSubmit}>
          <Box className="delete-account-fields">
            <TextField
              fullWidth
              variant="standard"
              label={t('settings.restore-account-modal.password-label')}
              placeholder={t('settings.restore-account-modal.password-placeholder')}
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              {...formik.getFieldProps('password')}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(p => !p)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>

          <Box className="delete-account-actions">
            <Button
              variant="outlined"
              size="small"
              onClick={handleClose}
              disabled={formik.isSubmitting}
            >
              {t('settings.restore-account-modal.cancel')}
            </Button>
            <Button
              variant="contained"
              size="small"
              type="submit"
              disabled={!formik.values.password || formik.isSubmitting || !formik.isValid}
              startIcon={
                formik.isSubmitting
                  ? <CircularProgress size={16} color="inherit" />
                  : null
              }
            >
              {t('settings.restore-account-modal.submit')}
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  )
}

export default RestoreAccountModal
